import React, { useEffect, useRef, useState } from 'react'
import { Card, Form, Button, Row, Col, Spin, Table, Select } from 'antd'
import { FileExcelOutlined } from '@ant-design/icons'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatRoundedValue, wtRoundValue } from 'src/helpers/commons/utils'
import WidgetDatePicker from 'src/widgets/DatePickers/WidgetDatePicker'
import moment from 'moment'
import WidgetWarehouseSelector from 'src/widgets/Selectors/WidgetWarehouseSelector'
import BaseListing from 'src/BaseListings'
import { useDownloadExcel } from 'react-export-table-to-excel'
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector'
import ReportVersionTabs from './ReportVersionTabs'
import './stockAgingReport.css'

const normalizeWarehouseLocationIds = (value) => {
    if (Array.isArray(value)) {
        return value.map((id) => +id).filter((id) => id > 0)
    }

    if (value === undefined || value === null || value === '') {
        return []
    }

    return String(value)
        .split(',')
        .map((id) => +id)
        .filter((id) => id > 0)
}

const getWarehouseOptionValue = (warehouse) => +warehouse?.value || +warehouse?.id || 0

const StockReport = () => {
    const [form] = Form.useForm()
    const [isLoading, setIsLoading] = useState(false)
    const [dateRange, setDateRange] = useState(moment().format('YYYY-MM-DD'))
    const [stockData, setStockData] = useState([])
    const [allWarehouses, setAllWarehouses] = useState([])
    const [warehouses, setWarehouses] = useState([])
    const [WarehouseLocations, setWarehouseLocations] = useState([])
    const [WarehouseLocationIds, setWarehouseLocationIds] = useState([])
    const [Items, setItems] = useState([])
    const [WarehouseName, setWarehouseName] = useState('')
    const [WarehouseId, setWarehouseId] = useState(0)
    const [ItemName, setItemName] = useState('')
    const [ItemId, setItemId] = useState(0)
    const Base = new BaseListing('stock_report')
    const tableRef = useRef(null)

    const companyName = wtGetFromLocalStorage('company_name')

    const syncSelectedWarehouse = (nextWarehouses) => {
        const selectedWarehouseStillAllowed = (nextWarehouses || []).some(
            (warehouse) => getWarehouseOptionValue(warehouse) === +WarehouseId,
        )

        if (WarehouseId && !selectedWarehouseStillAllowed) {
            setWarehouseId(0)
            setWarehouseName('')
        }
    }

    const loadWarehousesForLocations = (warehouseLocationIds, fallbackWarehouses = allWarehouses) => {
        const normalizedLocationIds = normalizeWarehouseLocationIds(warehouseLocationIds)

        if (normalizedLocationIds.length === 0) {
            setWarehouses(fallbackWarehouses || [])
            syncSelectedWarehouse(fallbackWarehouses || [])
            return
        }

        Promise.all(
            normalizedLocationIds.map(
                (warehouseLocationId) =>
                    new Promise((resolve, reject) => {
                        wtApiCall(
                            'dropdown',
                            {
                                dropdown_names: 'warehouses',
                                warehouse_location_id: warehouseLocationId,
                            },
                            'get',
                            (result) => {
                                resolve(result?.data?.[0]?.warehouses || [])
                            },
                            (error) => reject(error),
                        )
                    }),
            ),
        )
            .then((results) => {
                const mergedWarehouses = []
                const seenWarehouseIds = new Set()

                results.flat().forEach((warehouse) => {
                    const warehouseId = getWarehouseOptionValue(warehouse)
                    if (!warehouseId || seenWarehouseIds.has(warehouseId)) {
                        return
                    }

                    seenWarehouseIds.add(warehouseId)
                    mergedWarehouses.push(warehouse)
                })

                setWarehouses(mergedWarehouses)
                syncSelectedWarehouse(mergedWarehouses)
            })
            .catch((error) => {
                lzNotifyErrorMessageFromResult(error)
                setWarehouses([])
                syncSelectedWarehouse([])
            })
    }

    const changeValues = (key, val) => {
        if (key === 'warehouse_id') {
            setWarehouseId(val.value)
            setWarehouseName(val.label)
        }
        if (key === 'item_id') {
            setItemId(val.value)
            setItemName(val.label)
        }
        if (key === 'warehouse_location_ids') {
            const nextLocationIds = val || []
            setWarehouseLocationIds(nextLocationIds)
            loadWarehousesForLocations(nextLocationIds)
        }
    }

    const fetchStockAgingRows = (params) =>
        new Promise((resolve, reject) => {
            wtApiCall(
                'reports/stock_aging_report',
                params,
                'get',
                (response) => {
                    if (Array.isArray(response?.data?.data)) {
                        resolve(response.data.data)
                        return
                    }

                    resolve(Array.isArray(response?.data) ? response.data : [])
                },
                (error) => {
                    reject(error)
                },
            )
        })

    const columns = [
        {
            title: 'Warehouse',
            dataIndex: ['warehouse'],
            key: 'warehouse',
        },
        {
            title: 'Item Code',
            dataIndex: ['item_code'],
            key: 'item_code',
        },
        {
            title: 'Item Name',
            dataIndex: ['item_name'],
            key: 'item_name',
        },
        {
            title: 'Brand',
            dataIndex: ['brand_name'],
            key: 'brand_name',
        },
        {
            title: 'Unit',
            dataIndex: ['unit_name'],
            key: 'unit_name',
        },
        {
            title: 'Qty In Hand',
            dataIndex: 'qty_in_hand',
            key: 'qty_in_hand',
            align: 'right',
            render: (value) => wtRoundValue(value, 0),
        },
        {
            title: <span style={{ fontSize: '0.8rem' }}>One Year</span>,
            align: 'center',
            children: [
                {
                    title: 'Qty',
                    dataIndex: 'qty_less_than_one_year',
                    key: 'qty_less_than_one_year',
                    align: 'right',
                    render: (value) => wtRoundValue(value, 0),
                },
                {
                    title: 'Value',
                    dataIndex: 'total_cost_less_than_one_year',
                    key: 'total_cost_less_than_one_year',
                    align: 'right',
                    render: (value) => wtFormatRoundedValue(value, "", false, 3),
                }
            ]
        },
        {
            title: <span style={{ fontSize: '0.8rem' }}>Two Year</span>,
            align: 'center',
            children: [
                {
                    title: 'Qty',
                    dataIndex: 'qty_less_than_two_year',
                    key: 'qty_less_than_two_year',
                    align: 'right',
                    render: (value) => wtRoundValue(value, 0),
                },
                {
                    title: 'Value',
                    dataIndex: 'total_cost_less_than_two_year',
                    key: 'total_cost_less_than_two_year',
                    align: 'right',
                    render: (value) => wtFormatRoundedValue(value, "", false, 3),
                }
            ]
        },
        {
            title: <span style={{ fontSize: '0.8rem' }}>Three Year</span>,
            align: 'center',
            children: [
                {
                    title: 'Qty',
                    dataIndex: 'qty_less_than_three_year',
                    key: 'qty_less_than_three_year',
                    align: 'right',
                    render: (value) => wtRoundValue(value, 0),
                },
                {
                    title: 'Value',
                    dataIndex: 'total_cost_less_than_three_year',
                    key: 'total_cost_less_than_three_year',
                    align: 'right',
                    render: (value) => wtFormatRoundedValue(value, "", false, 3),
                }
            ]
        },
        {
            title: <span style={{ fontSize: '0.8rem' }}>Four Year</span>,
            align: 'center',
            children: [
                {
                    title: 'Qty',
                    dataIndex: 'qty_less_than_four_year',
                    key: 'qty_less_than_four_year',
                    align: 'right',
                    render: (value) => wtRoundValue(value, 0),
                },
                {
                    title: 'Value',
                    dataIndex: 'total_cost_less_than_four_year',
                    key: 'total_cost_less_than_four_year',
                    align: 'right',
                    render: (value) => wtFormatRoundedValue(value, "", false, 3),
                }
            ]
        },
        {
            title: <span style={{ fontSize: '0.8rem' }}>Five Year</span>,
            align: 'center',
            children: [
                {
                    title: 'Qty',
                    dataIndex: 'qty_less_than_five_year',
                    key: 'qty_less_than_five_year',
                    align: 'right',
                    render: (value) => wtRoundValue(value, 0),
                },
                {
                    title: 'Value',
                    dataIndex: 'total_cost_less_than_five_year',
                    key: 'total_cost_less_than_five_year',
                    align: 'right',
                    render: (value) => wtFormatRoundedValue(value, "", false, 3),
                }
            ]
        },
        {
            title: <span style={{ fontSize: '0.8rem' }}>Older Than Five Year</span>,
            align: 'center',
            children: [
                {
                    title: 'Qty',
                    dataIndex: 'qty_greater_than_five_year',
                    key: 'qty_greater_than_five_year',
                    align: 'right',
                    render: (value) => wtRoundValue(value, 0),
                },
                {
                    title: 'Value',
                    dataIndex: 'total_cost_greater_than_five_year',
                    key: 'total_cost_greater_than_five_year',
                    align: 'right',
                    render: (value) => wtFormatRoundedValue(value, "", false, 3),
                }
            ]
        },
        {
            title: 'Total Value Of Stock',
            dataIndex: 'total_cost',
            key: 'total_cost',
            align: 'right',
            render: (value) => wtFormatRoundedValue(value, "", false, 3),
        },
    ]

    const getReportData = (overrideFilters = null) => {
        const filters = overrideFilters || {
            date: dateRange,
            warehouse_id: WarehouseId,
            item_id: ItemId,
            warehouse_location_ids: WarehouseLocationIds.join(',') || 0,
        }
        const normalizedWarehouseLocationIds = normalizeWarehouseLocationIds(
            filters.warehouse_location_ids,
        )

        setIsLoading(true)
        const baseParams = {
            date: filters.date,
            item_id: filters.item_id,
            warehouse_location_ids: normalizedWarehouseLocationIds.join(',') || 0,
        }

        const reportPromise = fetchStockAgingRows({
            ...baseParams,
            warehouse_id: +filters.warehouse_id || 0,
        })

        reportPromise
            .then((rows) => {
                setStockData(rows)
                setIsLoading(false)
            })
            .catch((error) => {
                lzNotifyErrorMessageFromResult(error)
                setStockData([])
                setIsLoading(false)
            })
    }

    const { onDownload } = useDownloadExcel({
        currentTableRef: tableRef.current,
        filename: 'Stock Aging Report',
        sheet: 'Sheet',
    })


    useEffect(() => {
        wtApiCall(
            'dropdown',
            {
                dropdown_names: 'warehouses,items,warehouse_locations_with_permissions,warehouse_locations',
            },
            'get',
            (result) => {
                const warehousesData = result?.data?.[0]?.warehouses || []
                const itemsData = result?.data?.[1]?.items || []
                const warehouseLocationsWithPermissions =
                    result?.data?.[2]?.warehouse_locations_with_permissions || []
                const warehouseLocations = result?.data?.[3]?.warehouse_locations || []
                const resolvedWarehouseLocations =
                    warehouseLocationsWithPermissions.length > 0
                        ? warehouseLocationsWithPermissions
                        : warehouseLocations

                setAllWarehouses(warehousesData)
                setItems(itemsData)
                setWarehouseLocations(resolvedWarehouseLocations)
                setWarehouses(warehousesData)
                syncSelectedWarehouse(warehousesData)

                getReportData({
                    date: dateRange,
                    warehouse_id: WarehouseId,
                    item_id: ItemId,
                    warehouse_location_ids: 0,
                })
            },
            (error) => {
                lzNotifyErrorMessageFromResult(error)
                getReportData()
            },
        )
    }, [])

    const stockAgingV1Content = (
        <>
            <Row gutter={[24, 24]}>
                <Col xs={12} sm={8} md={3}>
                    <label>As Of: *</label>
                    <WidgetDatePicker value={dateRange} setDate={setDateRange} />
                </Col>

                <Col xs={15} sm={10} md={5}>
                    <div className="form-group">
                        <label>Warehouse</label>
                        <WidgetWarehouseSelector
                            warehouses={warehouses}
                            onChange={(values) => changeValues('warehouse_id', values)}
                            displayValue={WarehouseName}
                            reloadDropdownData={() => Base.dropdownData('warehouses', {}, [setWarehouses], 1)}
                            value={WarehouseId}
                        />
                    </div>
                </Col>
                <Col xs={15} sm={10} md={5}>
                    <div className="form-group">
                        <label>Item</label>
                        <WidgetItemSelector
                            items={Items}
                            onChange={(values) => changeValues('item_id', values)}
                            displayValue={ItemName}
                            reloadDropdownData={() => Base.dropdownData('items', {}, [setItems], 1)}
                            placeHolder={'Select Item'}
                            value={ItemId}
                        />
                    </div>
                </Col>
                <Col xs={12} sm={8} md={5}>
                    <div className="form-group">
                        <label>Warehouse Location</label>
                        <Select
                            mode="multiple"
                            maxTagCount="responsive"
                            options={WarehouseLocations}
                            value={WarehouseLocationIds}
                            onChange={(values) => changeValues('warehouse_location_ids', values)}
                            placeholder="Select Location"
                            style={{ width: '100%' }}
                        />
                    </div>
                </Col>
                <Col xs={12} sm={8} md={5}>
                    <Button
                        style={{ marginTop: 30 }}
                        type="primary"
                        onClick={() => getReportData()}
                        disabled={isLoading}
                    >
                        Generate Report
                    </Button>
                    <Button
                        className="text-white"
                        style={{ marginTop: 30, backgroundColor: '#28A745' }}
                        onClick={onDownload}
                        icon={<FileExcelOutlined />}
                    />
                </Col>

            </Row>

            <br />
            <div style={{ width: '100%' }}>
                <table className="table table-bordered data-table" ref={tableRef}>

                    <Table
                        columns={columns}
                        dataSource={stockData}
                        pagination={false}
                        bordered
                        size="small"
                        rowKey={(record, index) => index}
                        loading={{ indicator: <Spin />, spinning: isLoading }}
                        rowClassName={(record) => record.warehouse === 'TOTAL' ? 'total-row' : ''}
                    />
                </table>

            </div>
        </>
    )

    return (
        <div
            className="container-fluid"
            style={{
                padding: '1em',
                backgroundColor: 'white',
                minHeight: '100vh',
                fontFamily: 'Helvetica',
            }}
        >
            <Card
                title={
                    <div className="stock-aging-report-title-block">
                        <div className="stock-aging-report-version-tabs">
                            <ReportVersionTabs activeKey="stock-aging-report" />
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <span className="stock-aging-report-title">{companyName + ' Stock Aging Report'}</span>
                        </div>
                    </div>
                }
                style={{ width: '100%' }}
            >
                {stockAgingV1Content}
            </Card>
        </div>
    )
}

export default StockReport
